import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AppProvider, Box, InlineStack, Spinner } from '@shopify/polaris';
import enTranslations from '@shopify/polaris/locales/en.json';
import '@shopify/polaris/build/esm/styles.css';
import { useQuery } from '@tanstack/react-query';
import { AppBridgeProvider } from './providers/AppBridgeProvider';
import { ScanProvider, useScanContext } from './providers/ScanContext';
import { ZeliumThemeProvider } from './theme';
import { ScanStatusPill } from './components/ScanStatusPill';
import { NotificationBanner } from './components/NotificationBanner';
import { OnboardingPage } from './pages/OnboardingPage';
import { ScanProgressPage } from './pages/ScanProgressPage';
import { ScanCompletePage } from './pages/ScanCompletePage';
import { ProductDetailPage } from './pages/ProductDetailPage';
import { ProductListPage } from './pages/ProductListPage';
import { SuggestionsPage } from './pages/SuggestionsPage';
import { IdentifierReportPage } from './pages/IdentifierReportPage';
import { DashboardPage } from './pages/DashboardPage';
import { CompliancePage } from './pages/CompliancePage';
import { BillingPage } from './pages/BillingPage';
import { FeedPage } from './pages/FeedPage';
import BulkOperationsPage from './pages/BulkOperationsPage';
import JobHistoryPage from './pages/JobHistoryPage';
import { SummaryProgressPage } from './pages/SummaryProgressPage';
import { CrawlSimulationPage } from './pages/CrawlSimulationPage';
import { BenchmarkPage } from './pages/BenchmarkPage';
import { WebhookSyncPage } from './pages/WebhookSyncPage';
import { SettingsPage } from './pages/SettingsPage';
import { FaqPage } from './pages/FaqPage';
import { FeatureGatedPage } from './components/FeatureGatedPage';
import { getOnboarding, api } from './services/api';
import type { OnboardingResponse } from './types';

/**
 * T037: Onboarding-aware root route.
 * Checks onboarding.done from GET /api/store/onboarding and routes:
 * - done=true  → Dashboard
 * - done=false → Onboarding wizard
 */
function RootRedirect(): React.ReactElement {
  const { data, isLoading } = useQuery<OnboardingResponse>({
    queryKey: ['onboarding'],
    queryFn: getOnboarding,
    staleTime: 60_000,
  });

  if (isLoading) {
    return (
      <Box minHeight="60vh" width="100%">
        <InlineStack align="center" blockAlign="center">
          <Box paddingBlockStart="2000">
            <Spinner size="large" />
          </Box>
        </InlineStack>
      </Box>
    );
  }

  if (data?.onboarding?.done) {
    return <Navigate to="/dashboard" replace />;
  }

  return <OnboardingPage />;
}

/**
 * Guard that redirects to onboarding if the merchant hasn't completed the
 * welcome wizard yet. Wraps all post-onboarding routes.
 */
function OnboardingGuard({ children }: Readonly<{ children: React.ReactElement }>): React.ReactElement {
  const { data, isLoading } = useQuery<OnboardingResponse>({
    queryKey: ['onboarding'],
    queryFn: getOnboarding,
    staleTime: 60_000,
  });

  if (isLoading) {
    return (
      <Box minHeight="60vh" width="100%">
        <InlineStack align="center" blockAlign="center">
          <Box paddingBlockStart="2000">
            <Spinner size="large" />
          </Box>
        </InlineStack>
      </Box>
    );
  }

  if (!data?.onboarding?.done) {
    return <Navigate to="/" replace />;
  }

  return children;
}

/**
 * T037: Protected dashboard route.
 * Redirects to onboarding if merchant hasn't completed it yet.
 */
function ProtectedDashboard(): React.ReactElement {
  // Prefetch dashboard data so DashboardPage doesn't show a second spinner
  useQuery({
    queryKey: ['dashboard'],
    queryFn: () => api.get('/store/dashboard'),
    staleTime: 30_000,
  });

  return <DashboardPage />;
}

/**
 * T050: AppShell — layout wrapper inside ScanProvider + BrowserRouter.
 * Renders the global ScanStatusPill, NotificationBanner, and routes.
 * Nav items for locked features remain visible with a lock indicator — see billing-plan-design.md.
 * Nav menu is hidden until the merchant completes the welcome wizard.
 */
function AppShell(): React.ReactElement {
  const { completedJobId, dismissCompleted } = useScanContext();

  const { data: onboardingData } = useQuery<OnboardingResponse>({
    queryKey: ['onboarding'],
    queryFn: getOnboarding,
    staleTime: 60_000,
  });

  const onboardingDone = onboardingData?.onboarding?.done ?? false;

  return (
    <Box paddingBlockEnd="1600">
      {onboardingDone && (
        <ui-nav-menu>
          <a href="/" rel="home">Home</a>
          <a href="/products">Products</a>
          <a href="/identifiers">Identifiers</a>
          <a href="/feed-settings">Feed</a>
          <a href="/sync">Sync</a>
          <a href="/benchmarks">Benchmarks</a>
          {/* Bulk Ops deferred — see documents/phase-5-bulk-ops.md */}
          {/* <a href="/bulk">Bulk Ops</a> */}
          <a href="/billing">Billing</a>
          <a href="/settings">Settings</a>
          <a href="/faq">FAQ</a>
        </ui-nav-menu>
      )}
      <Box paddingBlockEnd="200">
        <ScanStatusPill />
      </Box>
      {completedJobId && (
        <Box paddingBlockEnd="200">
          <NotificationBanner
            jobId={completedJobId}
            onDismiss={dismissCompleted}
          />
        </Box>
      )}
      <Routes>
        <Route path="/" element={<RootRedirect />} />
        {/* scan-progress and scan-complete are part of the onboarding flow — no guard needed */}
        <Route path="/scan-progress/:jobId" element={<ScanProgressPage />} />
        <Route path="/scan-complete/:jobId" element={<ScanCompletePage />} />
        <Route path="/dashboard" element={<OnboardingGuard><ProtectedDashboard /></OnboardingGuard>} />
        <Route path="/products" element={<OnboardingGuard><ProductListPage /></OnboardingGuard>} />
        <Route path="/products/:id" element={<OnboardingGuard><ProductDetailPage /></OnboardingGuard>} />
        <Route path="/products/:id/suggestions" element={<OnboardingGuard><SuggestionsPage /></OnboardingGuard>} />
        <Route path="/products/:id/compliance" element={<OnboardingGuard><CompliancePage /></OnboardingGuard>} />
        <Route path="/products/:id/simulation" element={<OnboardingGuard><FeatureGatedPage feature="crawlSimulation" featureName="AI Crawl Simulation"><CrawlSimulationPage /></FeatureGatedPage></OnboardingGuard>} />
        <Route path="/identifiers" element={<OnboardingGuard><IdentifierReportPage /></OnboardingGuard>} />
        <Route path="/billing" element={<OnboardingGuard><BillingPage /></OnboardingGuard>} />
        <Route path="/feed-settings" element={<OnboardingGuard><FeatureGatedPage feature="aiFeed" featureName="AI Product Feed"><FeedPage /></FeatureGatedPage></OnboardingGuard>} />
        <Route path="/bulk" element={<OnboardingGuard><FeatureGatedPage feature="bulkOps" featureName="Bulk Operations"><BulkOperationsPage /></FeatureGatedPage></OnboardingGuard>} />
        <Route path="/bulk/history" element={<OnboardingGuard><FeatureGatedPage feature="bulkOps" featureName="Bulk Operations"><JobHistoryPage /></FeatureGatedPage></OnboardingGuard>} />
        <Route path="/summary-progress/:jobId" element={<OnboardingGuard><SummaryProgressPage /></OnboardingGuard>} />
        <Route path="/benchmarks" element={<OnboardingGuard><FeatureGatedPage feature="benchmarking" featureName="Benchmarks"><BenchmarkPage /></FeatureGatedPage></OnboardingGuard>} />
        <Route path="/sync" element={<OnboardingGuard><FeatureGatedPage feature="webhookSync" featureName="Real-time Sync"><WebhookSyncPage /></FeatureGatedPage></OnboardingGuard>} />
        <Route path="/settings" element={<OnboardingGuard><SettingsPage /></OnboardingGuard>} />
        <Route path="/faq" element={<OnboardingGuard><FaqPage /></OnboardingGuard>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Box>
  );
}

export function App(): React.ReactElement {
  return (
    <AppProvider i18n={enTranslations}>
      <ZeliumThemeProvider>
        <AppBridgeProvider>
          <ScanProvider>
            <BrowserRouter>
              <AppShell />
            </BrowserRouter>
          </ScanProvider>
        </AppBridgeProvider>
      </ZeliumThemeProvider>
    </AppProvider>
  );
}
